import type { ReactElement } from 'react';
import { createBrowserRouter, Navigate, redirect, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { auth } from '@/core/auth/authService';
import { evaluateAuthGuard } from '@/core/auth/authGuard';
import { registerNavigator } from '@/core/navigation';
import { ApplicationTestingDashboard } from '@/features/home/ApplicationTestingDashboard';
import { ApplicationTestingTicketDetails } from '@/features/home/ApplicationTestingTicketDetails';
import { ApplicationTestingTickets } from '@/features/home/ApplicationTestingTickets';
import { SupportTicketDetails } from '@/features/home/SupportTicketDetails';
import { LoginPage } from '@/features/login/LoginPage';
import { AccountingOAuthCallback } from '@/features/tr-core/AccountingOAuthCallback';
import { TaxreturnViewTrialBalance } from '@/features/tr-core/TaxreturnViewTrialBalance';
import { manifestPages, manifestRedirects, type ManifestPage } from './routeManifest';
import { screens } from './screens';
import { MATRIX_PARAM, MatrixSwitch } from './MatrixSwitch';
import { NotMigrated } from './NotMigrated';
import { HomeLayout } from './layouts/HomeLayout';
import { KitGallery } from './KitGallery';
import { ManageLayout } from './layouts/ManageLayout';
import { SettingsLayout } from './layouts/SettingsLayout';
import { TaxreturnLayout } from './layouts/TaxreturnLayout';

/** Angular's AuthGuard, as a loader. It only redirects; it never returns data. */
async function guardLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const result = await evaluateAuthGuard(url.pathname + url.search, {
    getAccessToken: () => auth.getAccessToken(),
    signOut: (navigate) => auth.signOut(navigate),
  });
  if (!result.allow) {
    throw redirect(result.redirectTo);
  }
  return null;
}

const lastSegment = (url: string) => url.slice(url.lastIndexOf('/') + 1);

function elementFor(page: ManifestPage, implemented: Record<string, ReactElement>): ReactElement {
  return implemented[page.url] ?? <NotMigrated component={page.component} url={page.url} />;
}

/** Children of one area (`/home/manage`, `/home/settings`, `/home/taxreturns`) or of `/home` itself. */
function childrenOf(prefix: string, implemented: Record<string, ReactElement>): RouteObject[] {
  const pages = manifestPages.filter(
    (page) => page.url !== '/login' && page.url.slice(0, page.url.lastIndexOf('/')) === prefix,
  );

  const routes: RouteObject[] = [];
  const matrixTable: Record<string, ReactElement> = {};

  for (const page of pages) {
    if (page.matrix.length > 0) {
      matrixTable[lastSegment(page.url)] = elementFor(page, implemented);
    } else {
      routes.push({ path: lastSegment(page.url), element: elementFor(page, implemented) });
    }
  }

  if (Object.keys(matrixTable).length > 0) {
    routes.push({ path: `:${MATRIX_PARAM}`, element: <MatrixSwitch table={matrixTable} /> });
  }

  for (const redirectRoute of manifestRedirects.filter((r) => r.from.slice(0, r.from.lastIndexOf('/')) === prefix)) {
    routes.push({ path: lastSegment(redirectRoute.from), element: <Navigate to={redirectRoute.to} replace /> });
  }
  const indexRedirect = manifestRedirects.find((r) => r.from === prefix);
  if (indexRedirect) {
    routes.push({ index: true, element: <Navigate to={indexRedirect.to} replace /> });
  }
  return routes;
}

/** The whole route tree, mirroring the Angular routing modules (see docs/migration/ROUTES.md). */
export function buildRoutes(implemented: Record<string, ReactElement> = screens): RouteObject[] {
  return [
    { path: '/login', element: <LoginPage /> },
    // BUG-038: where Xero, QuickBooks and FreeAgent return the user after the sign-in popup (a new page, not in Angular).
    { path: '/oauth/callback', loader: guardLoader, element: <AccountingOAuthCallback /> },
    ...(import.meta.env.DEV ? [{ path: '/__kit', element: <KitGallery /> }] : []),
    // The site root only ever redirects (to /login or /home/dashboard).
    { path: '/', loader: guardLoader, element: null },
    {
      path: '/home',
      loader: guardLoader,
      // Angular ran the guard once on entering /home, not on every navigation below it.
      shouldRevalidate: () => false,
      element: <HomeLayout />,
      children: [
        ...childrenOf('/home', implemented),
        // No Angular equivalent (a new feature), so it is not in routeManifest/childrenOf.
        { path: 'application-testing', element: <ApplicationTestingTickets /> },
        { path: 'application-testing/dashboard', element: <ApplicationTestingDashboard /> },
        { path: 'application-testing/:id', element: <ApplicationTestingTicketDetails /> },
        // APT239: one support ticket and its thread of replies (3.0's Tickets/Ticket.aspx); the list stays at /home/ticket.
        { path: 'ticket/:id', element: <SupportTicketDetails /> },
        { path: 'manage', element: <ManageLayout />, children: childrenOf('/home/manage', implemented) },
        { path: 'settings', element: <SettingsLayout />, children: childrenOf('/home/settings', implemented) },
        {
          path: 'taxreturns',
          element: <TaxreturnLayout />,
          children: [
            ...childrenOf('/home/taxreturns', implemented),
            // APT366: the read-only view of a trial balance (a new page, so not in routeManifest).
            { path: 'tasks-viewtrialbalance/:id', element: <TaxreturnViewTrialBalance /> },
          ],
        },
      ],
    },
  ];
}

export function createAppRouter() {
  const router = createBrowserRouter(buildRoutes());
  registerNavigator((to) => {
    void router.navigate(to);
  });
  return router;
}
